import {
	Badge,
	Button,
	Group,
	Loader,
	SegmentedControl,
	Text,
	Tooltip,
} from "@mantine/core";
import IconCheck from "@src/assets/icons/tabler/check.svg?react";
import clsx from "clsx";
import { useState } from "react";
import { useMarkAllMentionsAsRead } from "../mutations/mark-as-read";
import {
	fetchMentionInbox,
	useMentionInbox,
	useMentionUnreadCount,
} from "../queries/mention-inbox";
import type { MentionInboxItem } from "../schema";
import { MentionInboxItemComponent } from "./mention-inbox-item";
import { useMentionDeepLink } from "./use-mention-deep-link";

type InboxFilter = "all" | "unread";

const FILTER_VALUES = {
	all: "all",
	unread: "unread",
} as const satisfies Record<InboxFilter, InboxFilter>;

export function MentionInboxPopover(props: { onClose?: () => void }) {
	const { onClose } = props;
	const [filter, setFilter] = useState<InboxFilter>("unread");
	const { data: unreadCount = 0 } = useMentionUnreadCount();
	const { mutate: markAllAsRead, isPending: isMarkingAll } =
		useMarkAllMentionsAsRead();

	return (
		<div
			className={clsx(
				"flex w-96 flex-col overflow-hidden rounded-xl",
				"bg-surface-default shadow-2xl border border-border-default",
			)}
		>
			<div
				className={clsx(
					"flex flex-col gap-2 border-b border-border-default",
					"px-4 py-3 bg-surface-subtle",
				)}
			>
				<div className="flex items-center justify-between">
					<Group gap="xs">
						<Text fw={600} size="sm">
							Mentions
						</Text>
						{unreadCount > 0 && (
							<Badge color="red" size="sm" variant="filled">
								{unreadCount}
							</Badge>
						)}
					</Group>

					{unreadCount > 0 && (
						<Tooltip label="Mark all as read" withArrow>
							<Button
								leftSection={<IconCheck className="size-3.5" />}
								loading={isMarkingAll}
								onClick={() => markAllAsRead()}
								size="compact-xs"
								variant="subtle"
							>
								Mark all read
							</Button>
						</Tooltip>
					)}
				</div>

				<SegmentedControl
					data={[
						{
							label: unreadCount > 0 ? `Unread (${unreadCount})` : "Unread",
							value: FILTER_VALUES.unread,
						},
						{ label: "All", value: FILTER_VALUES.all },
					]}
					fullWidth
					onChange={(val) => {
						if (val === FILTER_VALUES.all || val === FILTER_VALUES.unread) {
							setFilter(val);
						}
					}}
					size="xs"
					value={filter}
				/>
			</div>

			<MentionInboxList filter={filter} key={filter} onClose={onClose} />
		</div>
	);
}

function MentionInboxList(props: {
	filter: InboxFilter;
	onClose?: () => void;
}) {
	const { filter, onClose } = props;
	const unreadOnly = filter === "unread";
	const { data, isLoading } = useMentionInbox({ unreadOnly });
	const handleDeepLink = useMentionDeepLink();

	const [extraItems, setExtraItems] = useState<MentionInboxItem[]>([]);
	const [pageState, setPageState] = useState<{
		nextCursor: string | null;
		hasMore: boolean;
	} | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);

	const nextCursor = pageState
		? pageState.nextCursor
		: (data?.nextCursor ?? null);
	const hasMore = pageState ? pageState.hasMore : (data?.hasMore ?? false);

	const handleSelect = (item: MentionInboxItem) => {
		handleDeepLink(item);
		onClose?.();
	};

	const items = [...(data?.items ?? []), ...extraItems];

	const handleLoadMore = async () => {
		if (!nextCursor || loadingMore) return;
		setLoadingMore(true);
		try {
			const page = await fetchMentionInbox({
				unreadOnly,
				after: nextCursor,
			});
			setExtraItems((prev) => [...prev, ...page.items]);
			setPageState({
				nextCursor: page.nextCursor ?? null,
				hasMore: page.hasMore,
			});
		} finally {
			setLoadingMore(false);
		}
	};

	return (
		<div className="max-h-[26rem] min-h-[14rem] overflow-y-auto">
			{isLoading ? (
				<div className="flex h-48 items-center justify-center">
					<Loader size={20} type="dots" />
				</div>
			) : items.length === 0 ? (
				<div
					className={clsx(
						"flex h-48 flex-col items-center justify-center",
						"p-6 text-center",
					)}
				>
					<div
						className={clsx(
							"flex size-10 items-center justify-center",
							"rounded-full bg-surface-subtle",
							"text-text-tertiary mb-2",
						)}
					>
						<IconCheck className="size-5" />
					</div>
					<Text c="dimmed" size="xs">
						{filter === "unread"
							? "You're all caught up! No unread mentions."
							: "No mentions yet."}
					</Text>
				</div>
			) : (
				<div className="divide-y divide-border-default/40">
					{items.map((item) => (
						<MentionInboxItemComponent
							item={item}
							key={item.id}
							onSelect={handleSelect}
						/>
					))}
					{hasMore && (
						<div className="p-2">
							<Button
								fullWidth
								loading={loadingMore}
								onClick={() => void handleLoadMore()}
								size="xs"
								variant="subtle"
							>
								Load more
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
